import React, { useState, useMemo } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { getFieldLabel } from '@/lib/fieldLabel';
import { getTooltipTheme } from '@/lib/tooltipTheme';

/**
 * Searchable "add filter / slicer" picker that groups the available fields by
 * their source TABLE (entity) so users see where each field comes from and can
 * type to filter the list. `dims` are the available (not-yet-added) field
 * objects. Shared by the Report Viewer filter bar and the Dashboard Builder's
 * Slicers / Global-Filter shelves so both surfaces behave identically.
 */
export function FilterFieldPicker({ dims, onPick, placeholder = '+ Add filter', testid, triggerClassName }) {
  const [open, setOpen] = useState(false);
  const tip = getTooltipTheme();
  const groups = useMemo(() => {
    const by = {};
    (dims || []).forEach(d => {
      const ent = d.entity_logical_name || d.entity_name || 'Fields';
      (by[ent] = by[ent] || []).push(d);
    });
    return Object.entries(by).sort((a, b) => a[0].localeCompare(b[0]));
  }, [dims]);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testid}
          className={triggerClassName || 'h-7 w-32 text-[9px] rounded-lg bg-muted/30 border border-border/30 flex-shrink-0 flex items-center justify-between px-2 text-muted-foreground hover:border-primary/40'}
        >
          <span className="truncate">{placeholder}</span>
          <svg className="w-3 h-3 opacity-60 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m6 9 6 6 6-6" /></svg>
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-64" align="start">
        <TooltipProvider delayDuration={400}>
        <Command>
          <CommandInput placeholder="Search fields or tables…" className="text-xs" />
          <CommandList>
            <CommandEmpty className="text-xs py-4 text-center text-muted-foreground">No fields found.</CommandEmpty>
            {groups.map(([ent, items]) => (
              <CommandGroup key={ent} heading={ent}>
                {items.map(d => (
                  <CommandItem
                    key={d.name}
                    value={`${getFieldLabel(d, d.name)} ${ent} ${d.name}`}
                    onSelect={() => { onPick(d.name); setOpen(false); }}
                    className="text-xs"
                    data-testid={`filter-field-${d.name}`}
                  >
                    <Tooltip delayDuration={300}>
                      <TooltipTrigger asChild>
                        <span className="truncate flex-1 cursor-pointer">{getFieldLabel(d, d.name)}</span>
                      </TooltipTrigger>
                      <TooltipContent
                        side="right"
                        align="start"
                        sideOffset={10}
                        avoidCollisions={false}
                        style={{ backgroundColor: tip.bg, color: tip.fg, borderColor: tip.border }}
                        className="max-w-[260px] border shadow-lg"
                      >
                        <div className="space-y-0.5">
                          {d.description && <p className="text-xs" style={{ color: tip.fg }}>{d.description}</p>}
                          <p className="text-[9px] font-mono" style={{ color: tip.sub }}>{d.name}</p>
                          <p className="text-[9px]" style={{ color: tip.sub }}>{d.entity_logical_name || d.entity_name || ent}</p>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                    <span className="ml-auto pl-2 text-[9px] text-muted-foreground/60 truncate">{ent}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
        </TooltipProvider>
      </PopoverContent>
    </Popover>
  );
}
